import { fetch, addTask } from "domain-task";
import { Dictionary } from "appz";
import * as qs from "query-string";
import { COOKIE_USER_JWT } from "ui/constants";
import Cookies from "js-cookie";

export interface ApiResponse {
    success: boolean;
    message?: string;
    status: number;
    data?: Dictionary<any>;
}

const credentials = "include";

const headers = {
    Accept:
        "application/json,text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,image/apng,*/*;q=0.8",
};

const getCsrfToken = (): Dictionary<string> => {
    const token = <HTMLInputElement>(
        document.getElementsByName("__RequestVerificationToken")[0]
    );

    return {
        __RequestVerificationToken: token ? token.value : "",
    };
};

const getAccessControl = () => ({
    // 'Access-Control-Allow-Origin': '*',
});

const getAuthorizationHeader = () => {
    var savedToken = Cookies.get(COOKIE_USER_JWT);
    if (savedToken) {
        return {
            Authorization: savedToken,
        };
    }

    return {};
};

const getContentType = (m: string): Dictionary<string> => ({
    "Content-Type": `${
        m === "POST" ? "application/json" : "application/x-www-form-urlencoded"
    }; charset=utf-8;`,
});

const getBody = (m: string, d: Dictionary<any>): string | undefined => {
    if (m !== "POST") {
        return undefined;
    }

    return JSON.stringify({
        ...getCsrfToken(),
        ...d,
    });
};

const getUrl = (m: string, u: string, d: Dictionary<any>): string =>
    m === "POST" ? u : `${u}?${qs.stringify(d, { arrayFormat: "bracket" })}`;

const getHeaders = (
    local: boolean,
    m: string,
    h: Dictionary<any>,
): Dictionary<any> => ({
    ...headers,
    ...h,
    ...(local ? getCsrfToken() : {}),
    ...getContentType(m),
    ...getAccessControl(),
    ...getAuthorizationHeader(),
});

const getCredentials = (local: boolean): RequestCredentials =>
    local ? credentials : "omit";

const getResult = (
    res: any,
    r: string | null,
): Promise<Dictionary<any> | string | null> => {
    if (r === null) {
        return Promise.resolve(null);
    }
    return r === "json"
        ? res.json()
        : r === "text"
        ? res.text()
        : r === "blob"
        ? res.blob()
        : Promise.resolve(res);
};

const getMode = (): RequestMode => "cors";
const getRedirect = (): RequestRedirect => "manual";
const getReferrer = (): string => "no-referrer";

const api = async function (
    m: string,
    r: string | null,
    u: string,
    d = {},
    h = {},
): Promise<ApiResponse> {
    const local = !u.startsWith("http");

    const method = m.toUpperCase();
    const headers = getHeaders(local, method, h);
    const body = getBody(method, d);
    const url = getUrl(method, u, d);
    const credentials = getCredentials(local);
    const mode = getMode();
    const redirect = getRedirect();
    const referrer = getReferrer();

    try {
        const fetchTask = fetch(url, {
            body,
            method,
            headers,
            credentials,
            mode,
            redirect,
            referrer,
        });

        addTask(fetchTask);

        const response = await fetchTask;

        if (!response.ok) {
            const message = await (<Promise<string>>(
                getResult(response, "text")
            ));

            return {
                success: false,
                status: response.status,
                message,
            };
        }

        const data = await (<Promise<Dictionary<any>>>getResult(response, r));

        return {
            success: true,
            status: response.status,
            data,
        };
    } catch (e) {
        return {
            success: false,
            status: 418,
            message: e.message || "Unexpected Error",
        };
    }
};

const get = api.bind(null, "GET", null);
const post = api.bind(null, "POST", null);
const put = api.bind(null, "PUT", null);
const del = api.bind(null, "DELETE", null);

const json: Dictionary<any> = {};
const text: Dictionary<any> = {};
const blob: Dictionary<any> = {};
["get", "post", "put", "delete"].forEach((m) => {
    json[m] = api.bind(null, m, "json");
    text[m] = api.bind(null, m, "text");
    blob[m] = api.bind(null, m, "blob");
});

export { get, post, put, del, json, text, blob };
